import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./UserMemberLifestyleInfo.module.scss";
import api from "@/utils/axios";
import { toast } from "react-toastify";

import {
  SEXUAL_IDENTITIES,
  SEXUAL_ROLES,
  ATTRACTION_GENDERS,
  CONNECTION_TYPES,
  INTIMACY_TYPES,
  SEXUAL_FANTASIES,
  BODY_TYPES,
  BODY_ATTRACT,
  SEXUAL_ENVIRONMENTS,
  EVENT_ATTRACT,
  ALCOHOL_CANNABIS,
  BE_MATCHED,
  JOIN_HOST,
} from "@/enums/lifestyleEnums";

const UserMemberLifestyleInfo = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [lifestyle, setLifestyle] = useState(null);

  // Single-choice fields
  const [sexualIdentity, setSexualIdentity] = useState("");
  const [sexualRole, setSexualRole] = useState("");
  const [bodyType, setBodyType] = useState("");
  const [alcoholCannabis, setAlcoholCannabis] = useState("");
  const [joinHost, setJoinHost] = useState("");

  // Multi-choice fields
  const [attractionGenders, setAttractionGenders] = useState([]);
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [intimacyTypes, setIntimacyTypes] = useState([]);
  const [sexualFantasies, setSexualFantasies] = useState([]);
  const [bodyAttract, setBodyAttract] = useState([]);
  const [sexualEnvironment, setSexualEnvironment] = useState([]);
  const [eventAttract, setEventAttract] = useState([]);
  const [beMatched, setBeMatched] = useState([]);

  const formatLabel = (v) => {
    if (!v) return "";
    return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const toggleInArray = (value, current, setter) => {
    if (current.includes(value)) {
      setter(current.filter((v) => v !== value));
    } else {
      setter([...current, value]);
    }
  };

  const loadLifestyle = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const res = await api.get(
        `/v1/admin/members/single/lifestyle-info/${userId}`
      );
      const data = res.data?.lifestyle;
      setLifestyle(data || null);

      if (data) {
        setSexualIdentity(data.sexual_identity || "");
        setSexualRole(data.sexual_role || "");
        setBodyType(data.body_type || "");
        setAlcoholCannabis(data.alcohol_cannabis || "");
        setJoinHost(data.join_host || "");

        setAttractionGenders(data.attraction_genders || []);
        setConnectionTypes(data.connection_types || []);
        setIntimacyTypes(data.intimacy_types || []);
        setSexualFantasies(data.sexual_fantasies || []);
        setBodyAttract(data.body_attract || []);
        setSexualEnvironment(data.sexual_environment || []);
        setEventAttract(data.event_attract || []);
        setBeMatched(data.be_matched || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load lifestyle info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLifestyle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!lifestyle) return;

    try {
      setSaving(true);

      await api.patch(`/v1/admin/members/single/lifestyle-info/${userId}`, {
        sexual_identity: sexualIdentity || undefined,
        sexual_role: sexualRole || undefined,
        body_type: bodyType || undefined,
        alcohol_cannabis: alcoholCannabis || undefined,
        join_host: joinHost || undefined,
        attraction_genders: attractionGenders,
        connection_types: connectionTypes,
        intimacy_types: intimacyTypes,
        sexual_fantasies: sexualFantasies,
        body_attract: bodyAttract,
        sexual_environment: sexualEnvironment,
        event_attract: eventAttract,
        be_matched: beMatched,
      });

      toast.success("Lifestyle info updated");
      await loadLifestyle();
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.join(", ") ||
        "Failed to update lifestyle info";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading && !lifestyle) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>Member Lifestyle Info</h1>
          <p className={cls.subtitle}>Loading lifestyle profile…</p>
        </div>
        <div className={cls.skeleton} />
      </div>
    );
  }

  if (!lifestyle) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>Member Lifestyle Info</h1>
          <p className={cls.subtitle}>
            No lifestyle profile found for this user.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      {/* Header */}
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back
        </button>
        <h1 className={cls.title}>Member Lifestyle Profile</h1>
        <p className={cls.subtitle}>
          Deep preferences, attraction map, and event vibe. Admin-only view to
          understand how this member wants to connect.
        </p>
      </div>

      {/* Meta bar */}
      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>User ID</span>
          <span className={cls.metaValue}>{lifestyle.userId}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Lifestyle ID</span>
          <span className={cls.metaValue}>{lifestyle.id}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Created</span>
          <span className={cls.metaValue}>
            {lifestyle.createdAt
              ? new Date(lifestyle.createdAt).toLocaleString()
              : "—"}
          </span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Updated</span>
          <span className={cls.metaValue}>
            {lifestyle.updatedAt
              ? new Date(lifestyle.updatedAt).toLocaleString()
              : "—"}
          </span>
        </div>
      </div>

      {/* Layout */}
      <div className={cls.layout}>
        {/* Left column: quick summary */}
        <section className={cls.summaryCard}>
          <div className={cls.cardHeader}>
            <h2 className={cls.cardTitle}>Quick Snapshot</h2>
            <p className={cls.cardSubtitle}>
              One-glance view of this member&apos;s lifestyle energy.
            </p>
          </div>

          <div className={cls.summaryGrid}>
            <div className={cls.summaryItem}>
              <span className={cls.summaryLabel}>Identity</span>
              <span className={cls.summaryValue}>
                {sexualIdentity ? formatLabel(sexualIdentity) : "Not set"}
              </span>
            </div>

            <div className={cls.summaryItem}>
              <span className={cls.summaryLabel}>Role</span>
              <span className={cls.summaryValue}>
                {sexualRole ? formatLabel(sexualRole) : "Not set"}
              </span>
            </div>

            <div className={cls.summaryItem}>
              <span className={cls.summaryLabel}>Body Type</span>
              <span className={cls.summaryValue}>
                {bodyType ? formatLabel(bodyType) : "Not set"}
              </span>
            </div>

            <div className={cls.summaryItem}>
              <span className={cls.summaryLabel}>Alcohol / Cannabis</span>
              <span className={cls.summaryValue}>
                {alcoholCannabis ? formatLabel(alcoholCannabis) : "Not set"}
              </span>
            </div>

            <div className={cls.summaryItemFull}>
              <span className={cls.summaryLabel}>Attracted Genders</span>
              <div className={cls.pillRow}>
                {attractionGenders.length ? (
                  attractionGenders.map((v) => (
                    <span key={v} className={cls.pill}>
                      {formatLabel(v)}
                    </span>
                  ))
                ) : (
                  <span className={cls.summaryValue}>Not selected</span>
                )}
              </div>
            </div>

            <div className={cls.summaryItemFull}>
              <span className={cls.summaryLabel}>Connection Types</span>
              <div className={cls.pillRow}>
                {connectionTypes.length ? (
                  connectionTypes.map((v) => (
                    <span key={v} className={cls.pill}>
                      {formatLabel(v)}
                    </span>
                  ))
                ) : (
                  <span className={cls.summaryValue}>Not selected</span>
                )}
              </div>
            </div>
          </div>

          <div className={cls.summaryFooter}>
            <span className={cls.summaryFooterLabel}>Event vibe</span>
            <div className={cls.pillRow}>
              {eventAttract.length ? (
                eventAttract.map((v) => (
                  <span key={v} className={cls.pillSoft}>
                    {formatLabel(v)}
                  </span>
                ))
              ) : (
                <span className={cls.summaryValue}>Not selected</span>
              )}
            </div>
          </div>
        </section>

        {/* Right column: editable form */}
        <section className={cls.formCard}>
          <div className={cls.cardHeader}>
            <h2 className={cls.cardTitle}>Edit Lifestyle Map</h2>
            <p className={cls.cardSubtitle}>
              Tune identity, attractions, environments and matching preferences.
            </p>
          </div>

          <form className={cls.form} onSubmit={handleSave}>
            {/* Identity & role */}
            <div className={cls.section}>
              <h3 className={cls.sectionTitle}>Core Identity</h3>
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Sexual identity</label>
                  <div className={cls.chipGroup}>
                    {SEXUAL_IDENTITIES.map((id) => (
                      <button
                        type="button"
                        key={id}
                        className={
                          sexualIdentity === id
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() =>
                          setSexualIdentity(sexualIdentity === id ? "" : id)
                        }
                      >
                        {formatLabel(id)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Sexual role</label>
                  <div className={cls.chipGroup}>
                    {SEXUAL_ROLES.map((role) => (
                      <button
                        type="button"
                        key={role}
                        className={
                          sexualRole === role
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() =>
                          setSexualRole(sexualRole === role ? "" : role)
                        }
                      >
                        {formatLabel(role)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Attraction & connection */}
            <div className={cls.section}>
              <h3 className={cls.sectionTitle}>Attraction & Connections</h3>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Attraction genders</label>
                  <div className={cls.chipGroupWrap}>
                    {ATTRACTION_GENDERS.map((g) => (
                      <label key={g} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={attractionGenders.includes(g)}
                          onChange={() =>
                            toggleInArray(
                              g,
                              attractionGenders,
                              setAttractionGenders
                            )
                          }
                        />
                        <span>{formatLabel(g)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Connection types</label>
                  <div className={cls.chipGroupWrap}>
                    {CONNECTION_TYPES.map((c) => (
                      <label key={c} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={connectionTypes.includes(c)}
                          onChange={() =>
                            toggleInArray(
                              c,
                              connectionTypes,
                              setConnectionTypes
                            )
                          }
                        />
                        <span>{formatLabel(c)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Intimacy types</label>
                  <div className={cls.chipGroupWrap}>
                    {INTIMACY_TYPES.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={intimacyTypes.includes(v)}
                          onChange={() =>
                            toggleInArray(v, intimacyTypes, setIntimacyTypes)
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Sexual fantasies</label>
                  <div className={cls.chipGroupWrap}>
                    {SEXUAL_FANTASIES.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={sexualFantasies.includes(v)}
                          onChange={() =>
                            toggleInArray(
                              v,
                              sexualFantasies,
                              setSexualFantasies
                            )
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Body & environment */}
            <div className={cls.section}>
              <h3 className={cls.sectionTitle}>Body & Environment</h3>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Body type</label>
                  <div className={cls.chipGroup}>
                    {BODY_TYPES.map((b) => (
                      <button
                        type="button"
                        key={b}
                        className={
                          bodyType === b
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() => setBodyType(bodyType === b ? "" : b)}
                      >
                        {formatLabel(b)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Attracted body traits</label>
                  <div className={cls.chipGroupWrap}>
                    {BODY_ATTRACT.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={bodyAttract.includes(v)}
                          onChange={() =>
                            toggleInArray(v, bodyAttract, setBodyAttract)
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Preferred environments</label>
                  <div className={cls.chipGroupWrap}>
                    {SEXUAL_ENVIRONMENTS.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={sexualEnvironment.includes(v)}
                          onChange={() =>
                            toggleInArray(
                              v,
                              sexualEnvironment,
                              setSexualEnvironment
                            )
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Event attraction</label>
                  <div className={cls.chipGroupWrap}>
                    {EVENT_ATTRACT.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={eventAttract.includes(v)}
                          onChange={() =>
                            toggleInArray(v, eventAttract, setEventAttract)
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Matching & substances */}
            <div className={cls.section}>
              <h3 className={cls.sectionTitle}>Matching & Party Style</h3>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Alcohol & cannabis</label>
                  <div className={cls.chipGroup}>
                    {ALCOHOL_CANNABIS.map((v) => (
                      <button
                        type="button"
                        key={v}
                        className={
                          alcoholCannabis === v
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() =>
                          setAlcoholCannabis(alcoholCannabis === v ? "" : v)
                        }
                      >
                        {formatLabel(v)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Be matched</label>
                  <div className={cls.chipGroupWrap}>
                    {BE_MATCHED.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={beMatched.includes(v)}
                          onChange={() =>
                            toggleInArray(v, beMatched, setBeMatched)
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Join / host style</label>
                  <div className={cls.chipGroup}>
                    {JOIN_HOST.map((v) => (
                      <button
                        type="button"
                        key={v}
                        className={
                          joinHost === v
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() => setJoinHost(joinHost === v ? "" : v)}
                      >
                        {formatLabel(v)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={loadLifestyle}
                disabled={saving}
              >
                Reset / Refresh
              </button>
              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default UserMemberLifestyleInfo;
